// ==========================================================================
// Squarebit Store - Copyright (c) 2026 Squarebit LLC. All rights reserved.
// Proprietary and confidential. Not for redistribution.
//
// Fetching the API reference the framework build shipped.
//
// The reference is generated per release and unpacked from the same archive
// the download comes from, so it always describes the build being offered. It
// is fetched rather than bundled for that reason: it belongs to the release,
// not to a deploy of this site.
//
// A null result is not a failure. It means the mirrored build predates the
// reference, which each page says in a sentence of its own.
// ==========================================================================

import { useQuery } from '@tanstack/react-query';
import { managementService } from '../../../management/service/management.service';
import { ApiModule, ApiPackageIndex, ApiRootIndex } from './types';

/**
 * Shared prefix, so every one of these lands in the same cache neighbourhood
 * and the whole reference can be dropped in one call if it ever needs to be.
 */
const KEY = 'sbframework-api';

export const useApiRootIndex = () =>
  useQuery({
    queryKey: [KEY],
    queryFn: () =>
      managementService.getFrameworkDocJson<ApiRootIndex>('api/index.json')
  });

// Both names come out of the URL bar and go into a path the server turns into
// a filename. The server resolves and bounds that path itself, which is the
// check that counts; encoding here keeps a stray separator from ever being
// sent in the first place.
const segment = (value: string) => encodeURIComponent(value);

export const useApiPackage = (packageName?: string) =>
  useQuery({
    queryKey: [KEY, packageName],
    queryFn: () =>
      managementService.getFrameworkDocJson<ApiPackageIndex>(
        `api/${segment(packageName as string)}/index.json`
      ),
    enabled: Boolean(packageName)
  });

export const useApiModule = (packageName?: string, moduleName?: string) =>
  useQuery({
    queryKey: [KEY, packageName, moduleName],
    queryFn: () =>
      managementService.getFrameworkDocJson<ApiModule>(
        `api/${segment(packageName as string)}/${segment(moduleName as string)}.json`
      ),
    enabled: Boolean(packageName && moduleName)
  });
